import { useState } from "react";
import { toast } from "sonner";
import { Search, Reply, Share2, Flag, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const C_STATUS = {
  new: "Nueva", notified_reporter: "Enviada", viewed_by_reporter: "Vista por reportante",
  reporter_replied: "Reportante respondió", more_info_requested: "Piden más info",
  contact_shared_by_responder: "Compartiste tu contacto", contact_shared_by_reporter: "Reportante compartió contacto",
  contact_shared_mutual: "Contacto mutuo", discarded: "Descartada",
  abuse_reported: "Abuso reportado", closed: "Cerrada", blocked: "Bloqueada",
};

export default function LookupRequest() {
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contacts/lookup", { tracking_code: code });
      setData(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const refresh = async () => { const { data } = await api.post("/contacts/lookup", { tracking_code: code }); setData(data); };
  const act = async (path, body, msg) => {
    try { await api.post(`/contacts/manage/${path}`, { tracking_code: code, ...body }); toast.success(msg); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!data) {
    return (
      <div className="max-w-md mx-auto space-y-5">
        <h1 className="font-heading text-2xl font-bold text-[#0F172A]">Consultar solicitud</h1>
        <p className="text-sm text-[#475569]">Ingresa tu código de seguimiento para revisar si el reportante respondió. Todo ocurre de forma protegida dentro de Reencuentro.</p>
        <form onSubmit={load} className="space-y-3 bg-white border border-[#E5E7EB] rounded-2xl p-5">
          <label className="block"><span className="text-xs font-semibold text-[#475569]">Código de seguimiento</span>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="XXXXXXXX" data-testid="lookup-request-code"
              className="mt-1 w-full h-11 px-3 rounded-lg border border-[#E5E7EB] font-mono uppercase" /></label>
          <button type="submit" disabled={busy} data-testid="lookup-request-submit"
            className="flex items-center justify-center gap-2 h-12 w-full rounded-xl bg-[#003893] text-white font-semibold disabled:opacity-50">
            <Search className="w-4 h-4" /> {busy ? "Consultando…" : "Consultar"}
          </button>
        </form>
      </div>
    );
  }

  const closed = ["discarded", "closed", "blocked"].includes(data.status);
  return (
    <div className="max-w-lg mx-auto space-y-4" data-testid="lookup-request-result">
      <button onClick={() => setData(null)} className="flex items-center gap-1 text-sm text-[#475569]"><ArrowLeft className="w-4 h-4" /> Consultar otra</button>
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono text-[#94A3B8]">{data.case_code}</span>
          <span className="text-xs font-semibold px-2 py-0.5 rounded bg-[#F1F5F9] text-[#475569]" data-testid="lookup-request-status">{C_STATUS[data.status] || data.status}</span>
        </div>
        {data.reporter_reply
          ? <p className="text-sm text-[#0F172A]" data-testid="lookup-reporter-reply"><b>Respuesta del reportante:</b> {data.reporter_reply}</p>
          : <p className="text-sm text-[#94A3B8]">El reportante aún no ha respondido.</p>}
        {data.responder_reply && <p className="text-xs text-[#475569]">Tu mensaje: {data.responder_reply}</p>}
        {data.reporter_contact && (
          <p className="text-sm text-[#065F46]" data-testid="lookup-reporter-contact">El reportante autorizó compartir su contacto: {data.reporter_contact.phone || "—"} {data.reporter_contact.email ? `· ${data.reporter_contact.email}` : ""}</p>
        )}
      </div>
      {!closed && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 space-y-3">
          <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Escribe un mensaje protegido…" data-testid="lookup-request-reply-input"
            className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => { if (!reply.trim()) { toast.error("Escribe un mensaje."); return; } act("reply", { message: reply }, "Mensaje enviado."); setReply(""); }}
              data-testid="lookup-request-reply" className="inline-flex items-center gap-1 h-10 px-4 rounded-lg bg-[#003893] text-white text-sm font-semibold"><Reply className="w-4 h-4" /> Responder</button>
            {!data.responder_share_consent && <button onClick={() => act("share-contact", {}, "Contacto compartido.")} data-testid="lookup-request-share" className="inline-flex items-center gap-1 h-10 px-4 rounded-lg bg-[#D1FAE5] text-[#065F46] text-sm font-semibold"><Share2 className="w-4 h-4" /> Autorizar compartir mi contacto</button>}
            <button onClick={() => act("abuse", {}, "Abuso reportado.")} data-testid="lookup-request-abuse" className="inline-flex items-center gap-1 h-10 px-4 rounded-lg bg-[#FEE2E2] text-[#991B1B] text-sm font-semibold"><Flag className="w-4 h-4" /> Reportar abuso</button>
          </div>
        </div>
      )}
    </div>
  );
}
